import {
    employeePortalStorageKey,
    hasEmployeePortalAccess,
    hasProducerPortalAccess,
    PortalAccessDiscovery,
    PortalContextKind,
    PortalContextSelection,
    workspacePortalStorageKey,
    producerPortalStorageKey,
} from '../models/portal-context';

const isActiveProducer = (producer: { AccessState: string }) => producer.AccessState === 'Active';

export const EMPLOYEE_PORTAL_PATH = '/EmployeePortal';
export const PRODUCER_PORTAL_PATH = '/ProducerPortal';

export const isEmployeePortalPath = (pathname: string): boolean =>
    pathname === EMPLOYEE_PORTAL_PATH || pathname.startsWith(`${EMPLOYEE_PORTAL_PATH}/`);

export const isProducerPortalPath = (pathname: string): boolean =>
    pathname === PRODUCER_PORTAL_PATH || pathname.startsWith(`${PRODUCER_PORTAL_PATH}/`);

export const isSupportedEmployeePortalRoute = (pathname: string): boolean =>
    pathname === EMPLOYEE_PORTAL_PATH
    || pathname === `${EMPLOYEE_PORTAL_PATH}/`
    || /^\/EmployeePortal\/Employment\/[^/]+\/Reviews\/[^/]+\/?$/.test(pathname);

export const isSupportedProducerPortalRoute = (pathname: string): boolean =>
    pathname === PRODUCER_PORTAL_PATH
    || pathname === `${PRODUCER_PORTAL_PATH}/`
    || pathname === `${PRODUCER_PORTAL_PATH}/ClientCompanies`
    || pathname === `${PRODUCER_PORTAL_PATH}/ClientCompanies/`
    || pathname === `${PRODUCER_PORTAL_PATH}/Team`
    || pathname === `${PRODUCER_PORTAL_PATH}/Team/`
    || pathname === `${PRODUCER_PORTAL_PATH}/Reports`
    || pathname === `${PRODUCER_PORTAL_PATH}/Reports/`
    || pathname === `${PRODUCER_PORTAL_PATH}/Payouts`
    || pathname === `${PRODUCER_PORTAL_PATH}/Payouts/`
    || pathname === `${PRODUCER_PORTAL_PATH}/Invitation`
    || pathname === `${PRODUCER_PORTAL_PATH}/Invitation/`;

export const isPortalContextAuthorized = (
    storedContext: string | null,
    discovery: PortalAccessDiscovery,
): boolean => {
    if (!storedContext)
        return false;

    if (storedContext === employeePortalStorageKey)
        return hasEmployeePortalAccess(discovery);

    if (storedContext.startsWith(`${PortalContextKind.Producer}:`))
        return hasProducerPortalAccess(discovery)
            && discovery.Producers?.some(producer =>
                producer.Id === storedContext.substring(`${PortalContextKind.Producer}:`.length)
                && isActiveProducer(producer));

    return discovery.Workspaces.some(workspace =>
        workspacePortalStorageKey(workspace.Id) === storedContext);
};

export const choosePortalContext = (
    discovery: PortalAccessDiscovery,
    storedContext: string | null,
    requestedPath: string,
): PortalContextSelection | null => {
    const activeProducers = discovery.Producers?.filter(isActiveProducer) ?? [];

    if (isEmployeePortalPath(requestedPath) && hasEmployeePortalAccess(discovery))
        return { Kind: PortalContextKind.Employee };

    if (isProducerPortalPath(requestedPath) && activeProducers.length > 0)
        return { Kind: PortalContextKind.Producer, ProducerCompanyId: activeProducers[0].Id };

    if (isPortalContextAuthorized(storedContext, discovery)) {
        if (storedContext === employeePortalStorageKey)
            return { Kind: PortalContextKind.Employee };

        if (storedContext.startsWith(`${PortalContextKind.Producer}:`))
            return {
                Kind: PortalContextKind.Producer,
                ProducerCompanyId: storedContext.substring(`${PortalContextKind.Producer}:`.length),
            };

        const organizationId = storedContext.substring(`${PortalContextKind.Workspace}:`.length);
        return { Kind: PortalContextKind.Workspace, OrganizationId: organizationId };
    }

    // Preserve the established company-workspace landing for people who are
    // authorized to work there. An identity with only employee relationships
    // never receives a workspace context and therefore lands directly in the
    // least-privileged personal portal.
    const firstWorkspace = discovery.Workspaces[0];
    const firstProducer = activeProducers[0];

    if (activeProducers.length === 1 && discovery.Workspaces.length === 0)
        return { Kind: PortalContextKind.Producer, ProducerCompanyId: firstProducer?.Id };

    if (discovery.Workspaces.length === 1 && activeProducers.length === 0)
        return { Kind: PortalContextKind.Workspace, OrganizationId: firstWorkspace.Id };

    if (firstWorkspace)
        return { Kind: PortalContextKind.Workspace, OrganizationId: firstWorkspace.Id };

    if (firstProducer)
        return { Kind: PortalContextKind.Producer, ProducerCompanyId: firstProducer.Id };

    if (hasEmployeePortalAccess(discovery))
        return { Kind: PortalContextKind.Employee };

    return null;
};

export const isProducerPortalContext = (context: PortalContextSelection | null): boolean =>
    context?.Kind === PortalContextKind.Producer;

export const producerPortalContext = (producerCompanyId: string): string =>
    producerPortalStorageKey(producerCompanyId);

export const portalLandingPathFromKind = (context: PortalContextKind): string =>
    context === PortalContextKind.Employee
        ? EMPLOYEE_PORTAL_PATH
        : context === PortalContextKind.Producer
            ? PRODUCER_PORTAL_PATH
            : '/';

export const portalLandingPath = (context: PortalContextSelection): string =>
    portalLandingPathFromKind(context.Kind);
